import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import WalletTransferModals from "../WalletPage/WalletTransferModals";
import { useWalletTransfer } from "../WalletPage/useWalletTransfer";
import FuturesAdjustIsolatedMarginModal from "../futures_trade/FuturesAdjustIsolatedMarginModal";
import {
  formatMarginModeLabel,
  isIsolatedMarginMode,
} from "../futures_trade/futuresSymbolMarginHelpers";
import "../FundingAccountPage/fundingAccount.css";
import "../AssetOverview/assetOverview.css";

const HISTORY_DP = 8;

function unwrapOpenPositions(res) {
  const d = res?.data;
  if (Array.isArray(d?.positions)) return d.positions;
  if (Array.isArray(d)) return d;
  if (Array.isArray(res?.positions)) return res.positions;
  return [];
}

function parseFuturesDecimal(v) {
  if (v == null || v === "") return NaN;
  if (typeof v === "object" && v !== null && "$numberDecimal" in v) {
    return parseFloat(v.$numberDecimal);
  }
  return parseFloat(v);
}

function parseBal(v) {
  const n = parseFuturesDecimal(v);
  return Number.isFinite(n) ? n : 0;
}

function fmt(val, decimals = 8) {
  const n = parseBal(val);
  if (n === 0) return "0.00";
  return parseFloat(n.toFixed(decimals)).toString();
}

/** BTCUSDT-PERP + USDT → /futures_trade/BTC_USDT */
function futuresPositionTradePath(pos) {
  const sym = String(pos?.symbol ?? "").trim();
  if (!sym) return "/futures_trade";
  const compact = sym.replace(/-PERP$/i, "").replace(/-PERPETUAL$/i, "");
  const quote = String(pos?.margin_asset || pos?.settlement_asset || "USDT").toUpperCase();
  if (compact.endsWith(quote) && compact.length > quote.length) {
    const base = compact.slice(0, -quote.length);
    return `/futures_trade/${base}_${quote}`;
  }
  return "/futures_trade";
}

function sideToneClass(side) {
  const s = String(side ?? "").toUpperCase();
  if (s === "LONG" || s === "BUY") return "text-success";
  if (s === "SHORT" || s === "SELL") return "text-danger";
  return "";
}

function sideAccentColor(side) {
  const s = String(side ?? "").toUpperCase();
  if (s === "LONG" || s === "BUY") return "#0ecb81";
  if (s === "SHORT" || s === "SELL") return "#f6465d";
  return "transparent";
}

function fmtTs(ts) {
  if (!ts) return "—";
  const m = new Date(ts);
  if (Number.isNaN(m.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${m.getFullYear()}-${pad(m.getMonth() + 1)}-${pad(m.getDate())} ${pad(m.getHours())}:${pad(m.getMinutes())}:${pad(m.getSeconds())}`;
}

function parseNum(v) {
  const n = parseFuturesDecimal(v);
  return Number.isFinite(n) ? n : NaN;
}

function capDec(n, dp = HISTORY_DP) {
  if (!Number.isFinite(n)) return null;
  return parseFloat(n.toFixed(dp)).toString();
}

function fmtPrice(n) {
  if (!Number.isFinite(n)) return "—";
  const capped = parseFloat(n.toFixed(HISTORY_DP));
  return capped.toLocaleString("en-US", {
    maximumFractionDigits: HISTORY_DP,
    minimumFractionDigits: 0,
  });
}

function fmtQty(n, dp = HISTORY_DP) {
  if (!Number.isFinite(n) || n <= 0) return "—";
  return parseFloat(n.toFixed(dp)).toString();
}

function fmtPct(n) {
  return Number.isFinite(n) ? `${capDec(n)}%` : "—";
}

function fmtUsdt(n, asset = "USDT") {
  if (!Number.isFinite(n)) return "—";
  return `${capDec(n)} ${asset}`;
}

function fmtSignedUsdt(n, asset = "USDT") {
  if (!Number.isFinite(n)) return "—";
  const body = capDec(Math.abs(n));
  const signed = n > 0 ? `+${body}` : n < 0 ? `-${body}` : body;
  return `${signed} ${asset}`;
}

function formatSymbolLabel(sym) {
  return String(sym ?? "").replace(/-PERP$/i, "").replace(/-PERPETUAL$/i, "") || "—";
}

function baseFromSymbol(sym, fallback = "BTC") {
  const s = String(sym ?? "");
  const m = s.match(/^([A-Z0-9]+)USDT/i);
  return m ? m[1].toUpperCase() : fallback;
}

/** Position Margin column — always initial_margin (Isolated and Cross). */
function resolvePositionMargin(pos) {
  return parseNum(pos?.initial_margin);
}

function resolveOpenPosition(pos) {
  const qty = parseNum(pos?.quantity);
  const entry = parseNum(pos?.average_entry_price ?? pos?.entry_price);
  const mark = parseNum(pos?.mark_price);
  const liq = parseNum(pos?.liquidation_price);
  const upnl = parseNum(pos?.unrealized_pnl);
  const roe = parseNum(pos?.roe_pct);
  const marginRatio = parseNum(pos?.margin_ratio);
  const margin = resolvePositionMargin(pos);
  return { qty, entry, mark, liq, upnl, roe, marginRatio, margin };
}

function accountInUse(acc) {
  return parseBal(acc?.locked_balance) + parseBal(acc?.isolated_reserved);
}

function accountTotal(acc) {
  return parseBal(acc?.available_balance) + accountInUse(acc);
}

function pnlClass(val) {
  const n = parseFuturesDecimal(val);
  if (!Number.isFinite(n)) return "";
  return n >= 0 ? "text-success" : "text-danger";
}

function FuturesTableEmptyBody({ colSpan }) {
  return (
    <tr className="no-data-row no-data-row2">
      <td colSpan={colSpan}>
        <div className="no-data-wrapper">
          <div className="no_data_vector">
            <img src="/images/no_data_vector.svg" className="dark_img" alt="" />
            <img src="/images/no_data_vector_light.png" className="light_img" alt="" />
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function FuturesAccountPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("assets");
  const [search, setSearch] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  const toggleSearch = useCallback(() => {
    setSearchActive((v) => !v);
  }, []);

  useEffect(() => {
    if (!searchActive) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 320);
    return () => window.clearTimeout(t);
  }, [searchActive]);

  useEffect(() => {
    if (!searchActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchActive(false);
    };
    const onClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchActive(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [searchActive]);
  const [hideSmall, setHideSmall] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [futuresRowPopup, setFuturesRowPopup] = useState(null);
  const [adjustMarginPos, setAdjustMarginPos] = useState(null);
  const [showBalance, setShowBalance] = useState(true);

  const mask = (val) => (showBalance ? val : "********");

  const closeTransferModal = useCallback(() => {
    const el = document.getElementById("walletTransferModal");
    if (el && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).hide();
    } else if (el) {
      el.classList.remove("show");
      el.style.display = "none";
      document.body.classList.remove("modal-open");
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, posRes] = await Promise.all([
        AuthService.futuresAccounts(),
        AuthService.futuresOpenPositions({ limit: 100 }),
      ]);
      if (accRes?.success) setAccounts(accRes.data?.accounts ?? []);
      if (posRes?.success) setPositions(unwrapOpenPositions(posRes));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const walletTransfer = useWalletTransfer({
    initialFrom: "spot",
    initialTo: "futures",
    onTransferSuccess: async () => {
      await fetchData();
      closeTransferModal();
    },
  });

  useEffect(() => {
    if (!futuresRowPopup) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setFuturesRowPopup(null); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [futuresRowPopup]);

  const totalAvailable = useMemo(() =>
    accounts.reduce((s, a) => s + parseBal(a.available_balance), 0),
    [accounts]);

  const totalLocked = useMemo(() =>
    accounts.reduce((s, a) => s + parseBal(a.locked_balance), 0),
    [accounts]);

  const totalIsolatedReserved = useMemo(() =>
    accounts.reduce((s, a) => s + parseBal(a.isolated_reserved), 0),
    [accounts]);

  const totalWalletBalance = useMemo(() =>
    accounts.reduce((s, a) => s + accountTotal(a), 0),
    [accounts]);

  const totalUnrealizedPnl = useMemo(() =>
    positions.reduce((s, p) => s + parseBal(p.unrealized_pnl), 0),
    [positions]);

  const filteredPositions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = hideSmall ? positions.filter((p) => parseBal(p.quantity) > 0) : positions;
    return q ? rows.filter((r) => r.symbol?.toLowerCase().includes(q)) : rows;
  }, [search, hideSmall, positions]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = hideSmall
      ? accounts.filter((a) => accountTotal(a) > 0)
      : accounts;
    return q
      ? rows.filter((r) => (r.margin_asset || "").toLowerCase().includes(q))
      : rows;
  }, [search, hideSmall, accounts]);

  const openTransfer = () => {
    walletTransfer.openTransferModal({ from: "spot", to: "futures" });
    const el = document.getElementById("walletTransferModal");
    if (el && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).show();
    } else if (el) {
      el.classList.add("show");
      el.style.display = "block";
      document.body.classList.add("modal-open");
    }
  };

  return (
    <div className="dashboard_right funding-account futures-account-page wallet-page--ao-popup">
      <div className="funding-account__header border-0">
        <div>
          <h1 className="funding-account__title">Futures Wallet</h1>
        </div>
        <div className="funding-account__header-actions">
          <button type="button" className="funding-account__pill" onClick={() => navigate("/futures_trade")}>
            Trade
          </button>
          <button type="button" className="funding-account__pill" onClick={() => openTransfer(null)}>
            Transfer
          </button>
        </div>
      </div>

      {/* Summary card */}
      <div className="funding-account__summarycard">
        <div className="funding-account__summary-top">
          <div className="funding-account__totals-label">Total Assets</div>
          <div className="funding-account__summary-icons" aria-hidden="true">
            <i
              className={showBalance ? "ri-eye-line" : "ri-eye-off-line"}
              style={{ cursor: "pointer" }}
              onClick={() => setShowBalance(!showBalance)}
            />
            {/* <i className="ri-file-list-3-line" /> */}
          </div>
        </div>

        <div className="funding-account__summary-main">
          <div className="funding-account__summary-amount">
            <div className="funding-account__summary-num">
              {loading ? "—" : mask(fmt(totalWalletBalance, 2))}
            </div>
            <div className="funding-account__summary-unit">USDT</div>
          </div>
          <div className="funding-account__summary-sub">
            ≈ {loading ? "—" : mask(fmt(totalWalletBalance, 2))} USD
          </div>
          <div className="funding-account__summary-pnl">
            <span>Unrealized PNL</span>
            <strong className={showBalance ? pnlClass(totalUnrealizedPnl) : "text-muted"}>
              {loading ? "—" : mask(`${totalUnrealizedPnl >= 0 ? "+" : ""}${totalUnrealizedPnl.toFixed(2)} USDT`)}
            </strong>
          </div>
        </div>

        <div className="funding-account__summary-grid">
          <div className="funding-account__summary-stat">
            <div className="funding-account__summary-stat-k">Available Balance</div>
            <div className="funding-account__summary-stat-v">
              {loading ? "—" : mask(`${fmt(totalAvailable, 2)} USDT`)}
            </div>
          </div>
          <div className="funding-account__summary-stat funding-account__summary-stat--right">
            <div className="funding-account__summary-stat-k">In Use</div>
            <div className="funding-account__summary-stat-v">
              {loading ? "—" : mask(`${fmt(totalLocked + totalIsolatedReserved, 2)} USDT`)}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="funding-account__tabs">
        <button
          type="button"
          className={`funding-account__tab ${tab === "assets" ? "is-active" : ""}`}
          onClick={() => setTab("assets")}
        >
          Assets
        </button>
        <button
          type="button"
          className={`funding-account__tab ${tab === "positions" ? "is-active" : ""}`}
          onClick={() => setTab("positions")}
        >
          Positions
        </button>
      </div>

      {/* Filters */}
      <div className="funding-account__filters funding-account__filters--tight rightsearch">
        <div
          ref={searchContainerRef}
          className={`searchBar custom-tabs ${searchActive ? "search_active" : ""}`}
          onClick={toggleSearch}
        >
          <i className="ri-search-2-line" aria-hidden="true" />
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            type="search"
            className="custom_search"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <label className="funding-account__check">
          <input type="checkbox" checked={hideSmall} onChange={(e) => setHideSmall(e.target.checked)} />
          Hide small balances
        </label>
      </div>

      {/* Assets tab */}
      {tab === "assets" && (
        <div className="funding-account__tablewrap">
          <table className="funding-account__table funding-account__table--futures-assets">
            <thead>
              <tr>
                <th>Asset</th>
                <th className="ta-r">Total Balance</th>
                <th className="ta-r">Available</th>
                <th className="ta-r">Isolated Reserved</th>
                <th className="ta-r">In Order</th>
                <th className="ta-r">Status</th>
                <th className="ta-r">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-3 text-muted">Loading…</td></tr>
              ) : filteredAccounts.length === 0 ? (
                <FuturesTableEmptyBody colSpan={7} />
              ) : filteredAccounts.map((acc) => (
                <tr
                  key={acc.account_id ?? acc.margin_asset}
                  onClick={(e) => {
                    if (window.innerWidth < 992 && !e.target.closest("a, button")) {
                      setFuturesRowPopup({ kind: "asset", row: acc });
                    }
                  }}
                >
                  <td data-label="Asset">
                    <div className="funding-account__coin flex-grow-1">
                      <span className="funding-account__coin-dot" aria-hidden="true" />
                      <div>
                        <div className="funding-account__coin-sym">{acc.margin_asset || "—"}</div>
                        <div className="funding-account__coin-name">Futures Margin</div>
                      </div>
                    </div>
                  </td>
                  <td className="ta-r agce-ao-total-balance-cell" data-label="Total Balance">
                    <div className="d-flex align-items-center justify-content-end gap-2 agce-ao-mobile-total-with-more">
                      <div className="agce-ao-balance-stack">
                        <span className="agce-ao-balance-stack__main">{mask(fmt(accountTotal(acc)))}</span>
                        <span className="agce-ao-balance-stack__fiat">{acc.margin_asset || "USDT"}</span>
                      </div>
                      <button
                        type="button"
                        className="agce-ao-more-btn d-inline-flex d-lg-none flex-shrink-0"
                        aria-label="View full row"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFuturesRowPopup({ kind: "asset", row: acc });
                        }}
                      >
                        <i className="ri-more-2-fill" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  <td className="ta-r" data-label="Available">{mask(fmt(acc.available_balance))}</td>
                  <td className="ta-r" data-label="Isolated Reserved">{mask(fmt(acc.isolated_reserved))}</td>
                  <td className="ta-r" data-label="In Order">{mask(fmt(acc.locked_balance))}</td>
                  <td className="ta-r" data-label="Status">
                    <span className={acc.account_status === "ACTIVE" ? "text-success" : "text-muted"}>
                      {acc.account_status || "—"}
                    </span>
                  </td>
                  <td className="ta-r" data-label="Action">
                    <div className="funding-account__actions">
                      <Link to="/futures_trade" className="funding-account__action">Trade</Link>
                      <button
                        type="button"
                        className="funding-account__action funding-account__action--btn"
                        onClick={() => openTransfer(acc.margin_asset)}
                      >
                        Transfer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Positions tab */}
      {tab === "positions" && (
        <div className="funding-account__tablewrap">
          <table className="funding-account__table funding-account__table--futures funding-account__table--futures-positions">
            <thead>
              <tr>
                <th>Symbol</th>
                <th className="ta-r">Size</th>
                <th>Entry Price</th>
                <th>Mark Price</th>
                <th>Liq. Price</th>
                <th>Margin Ratio</th>
                <th>Margin</th>
                <th>PNL (ROE%)</th>
                <th className="ta-r">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-3 text-muted">Loading…</td></tr>
              ) : filteredPositions.length === 0 ? (
                <FuturesTableEmptyBody colSpan={9} />
              ) : filteredPositions.map((pos) => {
                const { qty, entry, mark, liq, upnl, roe, marginRatio, margin } = resolveOpenPosition(pos);
                const baseAsset = baseFromSymbol(pos.symbol);
                const marginAsset = pos.margin_asset || pos.pnl_asset || pos.settlement_asset || "USDT";
                const tradePath = futuresPositionTradePath(pos);
                const isolated = isIsolatedMarginMode(pos.margin_mode);
                const modeLabel = formatMarginModeLabel(pos.margin_mode);
                return (
                  <tr
                    key={pos._id ?? pos.position_id ?? pos.symbol}
                    style={{ borderLeft: `3px solid ${sideAccentColor(pos.side)}` }}
                    onClick={(e) => {
                      if (window.innerWidth < 992 && !e.target.closest("a, button")) {
                        setFuturesRowPopup({ kind: "position", row: pos });
                      }
                    }}
                  >
                    <td data-label="Symbol">
                      <div className="funding-account__coin flex-grow-1">
                        <div>
                          <div className="funding-account__coin-sym">{formatSymbolLabel(pos.symbol)}</div>
                          <div className="small text-muted">
                            <span className={sideToneClass(pos.side)}>{pos.side}</span>
                            {pos.leverage != null ? ` · ${pos.leverage}x` : ""}
                            {modeLabel !== "—" ? ` · ${modeLabel}` : ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="ta-r agce-ao-total-balance-cell" data-label="Size">
                      <div className="d-flex align-items-center justify-content-end gap-2 agce-ao-mobile-total-with-more">
                        <div className="agce-ao-balance-stack">
                          <span className="agce-ao-balance-stack__main">
                            {fmtQty(qty)} {baseAsset}
                          </span>
                          <span className={`agce-ao-balance-stack__fiat d-lg-none ${pnlClass(upnl)}`}>
                            {fmtSignedUsdt(upnl, marginAsset)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="agce-ao-more-btn d-inline-flex d-lg-none flex-shrink-0"
                          aria-label="View full row"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFuturesRowPopup({ kind: "position", row: pos });
                          }}
                        >
                          <i className="ri-more-2-fill" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                    <td data-label="Entry Price">{fmtPrice(entry)}</td>
                    <td data-label="Mark Price">{fmtPrice(mark)}</td>
                    <td data-label="Liq. Price">{fmtPrice(liq)}</td>
                    <td data-label="Margin Ratio">{fmtPct(marginRatio)}</td>
                    <td data-label="Margin">
                      <div className="agce-ft-acct-pos-margin">
                        <div className="agce-ft-acct-pos-margin__text">
                          <span className="agce-ft-acct-pos-margin__value">{fmtUsdt(margin, marginAsset)}</span>
                          {pos.margin_mode ? (
                            <span className="agce-ft-acct-pos-margin__mode">({modeLabel})</span>
                          ) : null}
                        </div>
                        {isolated ? (
                          <button
                            type="button"
                            className="agce-ft-acct-pos-margin__edit"
                            aria-label="Adjust isolated margin"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAdjustMarginPos(pos);
                            }}
                          >
                            <i className="ri-edit-box-line" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="PNL (ROE%)">
                      <div className={pnlClass(upnl)}>{fmtSignedUsdt(upnl, marginAsset)}</div>
                      <div className={`small ${pnlClass(upnl)}`}>{roe >= 0 ? "+" : ""}{fmtPct(roe)}</div>
                    </td>
                    <td className="ta-r" data-label="Action">
                      <div className="funding-account__actions">
                        <Link to={tradePath} className="funding-account__action">Trade</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile detail popup */}
      {futuresRowPopup && (
        <>
          <div className="agce-ao-detail-backdrop" role="presentation" onClick={() => setFuturesRowPopup(null)} />
          <div
            className="modal fade show d-flex agce-ao-sheet-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            aria-labelledby="futuresRowPopupTitle"
            style={{ zIndex: 1060 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable agce-ao-sheet-dialog">
              <div className="modal-content agce-ao-sheet border-0">

                {futuresRowPopup.kind === "asset" && (
                  <>
                    <h2 id="futuresRowPopupTitle" className="visually-hidden">{futuresRowPopup.row.margin_asset} account details</h2>
                    <div className="agce-ao-sheet__header">
                      <div className="agce-ao-sheet__brand">
                        <span className="agce-ao-sheet__account-badge" aria-hidden="true">
                          {futuresRowPopup.row.margin_asset?.charAt(0) || "F"}
                        </span>
                        <div>
                          <div className="agce-ao-sheet__sym">{futuresRowPopup.row.margin_asset || "—"}</div>
                          <div className="agce-ao-sheet__fullname">Futures Margin</div>
                        </div>
                      </div>
                      <button type="button" className="btn-close agce-ao-sheet__close" aria-label="Close" onClick={() => setFuturesRowPopup(null)} />
                    </div>
                    <div className="modal-body agce-ao-sheet__body">
                      <div className="agce-ao-sheet__hero">
                        <div className="agce-ao-sheet__hero-num">{fmt(accountTotal(futuresRowPopup.row))}</div>
                        <div className="agce-ao-sheet__hero-fiat">{futuresRowPopup.row.margin_asset || "USDT"} Total Balance</div>
                      </div>
                      <div className="agce-ao-sheet__kvlist">
                        <div className="agce-ao-sheet__kv">
                          <span>Available</span>
                          <span className="agce-ao-sheet__kv-val">{fmt(futuresRowPopup.row.available_balance)}</span>
                        </div>
                        <div className="agce-ao-sheet__kv">
                          <span>Isolated Reserved</span>
                          <span className="agce-ao-sheet__kv-val">{fmt(futuresRowPopup.row.isolated_reserved)}</span>
                        </div>
                        <div className="agce-ao-sheet__kv">
                          <span>In Order</span>
                          <span className="agce-ao-sheet__kv-val">{fmt(futuresRowPopup.row.locked_balance)}</span>
                        </div>
                        <div className="agce-ao-sheet__kv">
                          <span>Status</span>
                          <span className={`agce-ao-sheet__kv-val ${futuresRowPopup.row.account_status === "ACTIVE" ? "text-success" : ""}`}>
                            {futuresRowPopup.row.account_status || "—"}
                          </span>
                        </div>
                      </div>
                      <div className="agce-ao-sheet__divider" />
                      <div className="agce-ao-sheet__table-actions agce-ao-sheet__table-actions--spot">
                        <Link to="/futures_trade" className="btn-link text-white" onClick={() => setFuturesRowPopup(null)}>
                          Trade
                        </Link>
                        <button
                          type="button"
                          className="btn-link text-white"
                          onClick={() => { setFuturesRowPopup(null); openTransfer(futuresRowPopup.row.margin_asset); }}
                        >
                          Transfer
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {futuresRowPopup.kind === "position" && (() => {
                  const pos = futuresRowPopup.row;
                  const { qty, entry, mark, liq, upnl, roe, marginRatio, margin } = resolveOpenPosition(pos);
                  const baseAsset = baseFromSymbol(pos.symbol);
                  const marginAsset = pos.margin_asset || pos.pnl_asset || pos.settlement_asset || "USDT";
                  const tradePath = futuresPositionTradePath(pos);
                  const isolated = isIsolatedMarginMode(pos.margin_mode);
                  const modeLabel = formatMarginModeLabel(pos.margin_mode);
                  const opened = pos.opened_at || pos.created_at;
                  return (
                    <>
                      <h2 id="futuresRowPopupTitle" className="visually-hidden">{pos.symbol} position details</h2>
                      <div className="agce-ao-sheet__header">
                        <div className="agce-ao-sheet__brand">
                          <span className="agce-ao-sheet__account-badge" aria-hidden="true">
                            {formatSymbolLabel(pos.symbol)?.charAt(0) || "F"}
                          </span>
                          <div>
                            <div className="agce-ao-sheet__sym">{formatSymbolLabel(pos.symbol)}</div>
                            <div className={`agce-ao-sheet__fullname ${sideToneClass(pos.side)}`}>
                              {pos.side || "—"}
                              {pos.leverage != null ? ` · ${pos.leverage}x` : ""}
                              {modeLabel !== "—" ? ` · ${modeLabel}` : ""}
                            </div>
                          </div>
                        </div>
                        <button type="button" className="btn-close agce-ao-sheet__close" aria-label="Close" onClick={() => setFuturesRowPopup(null)} />
                      </div>
                      <div className="modal-body agce-ao-sheet__body">
                        <div className="agce-ao-sheet__hero">
                          <div className="agce-ao-sheet__hero-num">{fmtQty(qty)} {baseAsset}</div>
                          <div className={`agce-ao-sheet__hero-fiat ${pnlClass(upnl)}`}>
                            {fmtSignedUsdt(upnl, marginAsset)}
                            {Number.isFinite(roe) ? ` (${roe >= 0 ? "+" : ""}${fmtPct(roe)})` : ""}
                          </div>
                        </div>
                        <div className="agce-ao-sheet__kvlist">
                          {opened ? (
                            <div className="agce-ao-sheet__kv">
                              <span>Opened</span>
                              <span className="agce-ao-sheet__kv-val">{fmtTs(opened)}</span>
                            </div>
                          ) : null}
                          <div className="agce-ao-sheet__kv">
                            <span>Entry Price</span>
                            <span className="agce-ao-sheet__kv-val">{fmtPrice(entry)}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Mark Price</span>
                            <span className="agce-ao-sheet__kv-val">{fmtPrice(mark)}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Liq. Price</span>
                            <span className="agce-ao-sheet__kv-val">{fmtPrice(liq)}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Margin Ratio</span>
                            <span className="agce-ao-sheet__kv-val">{fmtPct(marginRatio)}</span>
                          </div>
                          <div className="agce-ao-sheet__kv">
                            <span>Margin</span>
                            <span className="agce-ao-sheet__kv-val">
                              {fmtUsdt(margin, marginAsset)}
                              {pos.margin_mode ? ` (${modeLabel})` : ""}
                              {isolated ? (
                                <button
                                  type="button"
                                  className="agce-ft-acct-pos-margin__edit ms-2"
                                  aria-label="Adjust isolated margin"
                                  onClick={() => {
                                    setFuturesRowPopup(null);
                                    setAdjustMarginPos(pos);
                                  }}
                                >
                                  <i className="ri-edit-box-line" aria-hidden />
                                </button>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        <div className="agce-ao-sheet__divider" />
                        <div className="agce-ao-sheet__table-actions agce-ao-sheet__table-actions--spot">
                          <Link to={tradePath} className="btn-link text-white" onClick={() => setFuturesRowPopup(null)}>
                            Trade
                          </Link>
                        </div>
                      </div>
                    </>
                  );
                })()}

              </div>
            </div>
          </div>
        </>
      )}

      {adjustMarginPos && isIsolatedMarginMode(adjustMarginPos.margin_mode) ? (
        <FuturesAdjustIsolatedMarginModal
          pos={adjustMarginPos}
          onClose={() => setAdjustMarginPos(null)}
        />
      ) : null}

      {/* The shared Funds Transfer modal — same design as all other wallet pages */}
      <WalletTransferModals
        fromWalletType={walletTransfer.fromWalletType}
        setFromWalletType={walletTransfer.setFromWalletType}
        toWalletType={walletTransfer.toWalletType}
        setToWalletType={walletTransfer.setToWalletType}
        availableWalletTypes={walletTransfer.availableWalletTypes}
        walletLabelsMap={walletTransfer.walletLabelsMap}
        selectedCurrency={walletTransfer.selectedCurrency}
        setSelectedCurrency={walletTransfer.setSelectedCurrency}
        transferAmount={walletTransfer.transferAmount}
        setTransferAmount={walletTransfer.setTransferAmount}
        transferable={walletTransfer.transferable}
        balanceLoading={walletTransfer.balanceLoading}
        sourceHint={walletTransfer.sourceHint}
        handleInterchangeWallet={walletTransfer.handleInterchangeWallet}
        handleTransfer={walletTransfer.handleTransfer}
        currencyData={walletTransfer.currencyData}
        fiveDecimal={walletTransfer.fiveDecimal}
        marginInvolved={walletTransfer.marginInvolved}
        marginPairs={walletTransfer.marginPairs}
        selectedMarginPair={walletTransfer.selectedMarginPair}
        setSelectedMarginPair={walletTransfer.setSelectedMarginPair}
        marginAssetType={walletTransfer.marginAssetType}
        setMarginAssetType={walletTransfer.setMarginAssetType}
        marginPairDropOpen={walletTransfer.marginPairDropOpen}
        setMarginPairDropOpen={walletTransfer.setMarginPairDropOpen}
      />
    </div>
  );
}
